<script lang="ts">
	import Mill from '$lib/logos/mill.svelte';
	import { headerVisible, lockHeader, subNav } from '$lib/stores/header';
	import ScrollDetector from '$lib/scrollDetector.svelte';
	import { page } from '$app/state';
	import type { ScrollDirection } from '$lib/types/scroll';
	import { afterNavigate, beforeNavigate } from '$app/navigation';
	import { event } from '$lib/utils/tracking';
	import SubNavigation from './subNavigation.svelte';
	import { reservationUrl } from '$lib/stores/utmParams';
	import type { SubNav } from './types';
	import BtnPrimary from '$lib/buttons/v2/btnPrimary.svelte';
	import { browser } from '$app/environment';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import ShoppingCartIcon from '$lib/icons/shoppingCartIcon.svelte';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import { device } from '$lib/state/device.svelte';

	let bannerHeight = $state(0);
	let headerHeight = $state(999);

	function hasEntries(nav: SubNav | undefined): nav is SubNav {
		return nav !== undefined && nav.entries.length > 0;
	}

	let heroElement: HTMLElement | null = null;

	function isSolid(scrollY = 0) {
		if (hasEntries($subNav)) return true;

		if (!browser) return false;

		const heroHeight = heroElement?.clientHeight || 0;

		if (page.route.id === '/(marketing)/food-recycler') return false;

		return (
			heroHeight === bannerHeight ||
			(heroHeight > bannerHeight && scrollY >= heroHeight - headerHeight)
		);
	}

	let solid = $state(isSolid());

	let showOrderButton = $derived(
		!page.route.id ||
			(!page.route.id.startsWith('/(marketing)/order-now') &&
				!['/(marketing)/food-recycler', '/(marketing)/friend', '/(marketing)/gift-card'].includes(
					page.route.id
				))
	);

	let logoColor = $derived(
		page.route.id === '/(marketing)' && !solid ? 'text-stone-5' : 'text-green-3'
	);
	let orderNowPage = $derived(page.route.id === '/(marketing)/order-now');
	let pdpPage = $derived(page.route.id === '/(marketing)/food-recycler');

	let transitionColor = $state(false);

	let buttonWidth = $state(999);

	let scrollDir = $state<ScrollDirection>('down');
	let hideHeaderInitially = $state(false);

	let transformY = $derived(solid || pdpPage ? 0 : device.data.isDesktop ? 16 : 0);
	let transformX = $derived(solid ? 0 : buttonWidth + (device.data.isDesktop ? 16 : 12));

	function setSolid(scrollY: number) {
		solid = isSolid(scrollY);
	}

	function toggleMainHeader(dir: ScrollDirection) {
		if (!$lockHeader && hasEntries($subNav)) {
			setHeaderVisible(dir === 'up' || window.scrollY === 0, $subNav, headerHeight);
		}
	}

	function handleScrollStart(dir: ScrollDirection) {
		transitionColor = true;
		toggleMainHeader(dir);
	}

	function setHeaderVisible(shouldBeVisible = true, sub: SubNav | undefined, headerHeight: number) {
		const hasSubNav = hasEntries(sub);

		let visible = !hasSubNav || shouldBeVisible;
		let offset = visible ? 0 : -headerHeight;

		if (!shouldBeVisible && hasSubNav && sub.type === 'anchors' && window.scrollY >= headerHeight) {
			visible = false;
			offset = -headerHeight;
		}

		headerVisible.set(visible);
	}

	function handleScroll(y: number) {
		if (hideHeaderInitially && y === 0) {
			hideHeaderInitially = false;
			setHeaderVisible(false, $subNav, headerHeight);
		} else {
			setHeaderVisible(y === 0 || scrollDir === 'up', $subNav, headerHeight);
		}
		setSolid(y);
	}

	function handleScrollDirChange(dir: ScrollDirection) {
		scrollDir = dir;
		toggleMainHeader(dir);
	}

	beforeNavigate(() => {
		transitionColor = false;
	});

	afterNavigate(({ from, to }) => {
		heroElement = document.getElementById('hero');

		if (to?.route.id === '/(marketing)') {
			transitionColor = false;
		}

		if (from?.route.id !== to?.route.id) {
			headerVisible.set(true);
		}

		setSolid(window.scrollY);

		if (to?.route.id === '/(marketing)/food-recycler') {
			hideHeaderInitially = true;
		}
	});

	function handleReserveClick() {
		event('Top Navigation Reserve Button Clicked');
	}
</script>

<ScrollDetector
	onstart={handleScrollStart}
	onscroll={handleScroll}
	onchange={handleScrollDirChange}
/>

<header id="site-header" class="fixed inset-x-0 top-0 z-50" class:absolute={pdpPage}>
	<div
		class="relative z-10 transition-transform ease-out will-change-transform"
		style="transform: translate3d(0, var(--banner-height), 0)"
		class:duration-400={!$headerVisible}
		class:duration-600={$headerVisible}
	>
		<nav
			class="relative z-20 flex flex-col justify-center"
			class:transition-colors={transitionColor}
			class:duration-600={transitionColor}
			class:ease-out={transitionColor}
			class:bg-white={solid || orderNowPage}
			bind:clientHeight={headerHeight}
		>
			<div class="px-6 md:px-8 xl:px-12">
				<div
					class="flex items-center py-[13px] lg:py-5"
					class:justify-between={!orderNowPage}
					class:justify-center={orderNowPage}
				>
					{#if orderNowPage}
						<a
							href="/food-recycler"
							class="absolute left-0 rounded-full p-4 md:left-4 lg:left-8 lg:bg-white"
						>
							<ArrowLeft class="h-6 w-6 text-brown-2 " />
						</a>
					{/if}

					<div
						class="pointer-events-none flex transition-transform duration-1000 max-lg:mr-container-mobile"
						style="transform: translate3d(0, {transformY}px, 0)"
						class:xl:flex-1={!orderNowPage}
						class:w-full={orderNowPage}
						class:justify-center={orderNowPage}
					>
						<a href="/" title="Home" class="pointer-events-auto">
							<Mill class="h-[30px] w-[44.03px] lg:h-10 lg:w-[58.7px] {logoColor}" />
						</a>
					</div>

					<div
						class="flex flex-1 items-center justify-end gap-x-4 transition-transform duration-1000 lg:gap-x-8"
						class:delay-100={!solid}
						style="transform: translate3d({transformX}px,{transformY}px,0)"
					>
						{#if showOrderButton}
							<Button
								title="Open shopping cart"
								class="btn-tertiary btn-white"
								data-testid="shopping-cart-open-btn"
								onclick={() => shoppingCart.open()}
							>
								<ShoppingCartIcon />
							</Button>
							<div
								class="transition-transform duration-[1000ms]"
								class:delay-100={solid}
								style="transform: translate3d({transformX}px,0,0)"
								bind:clientWidth={buttonWidth}
							>
								<BtnPrimary
									size="small"
									class="lg:hidden"
									href="/food-recycler"
									onclick={handleReserveClick}
								>
									{$reservationUrl.label}
								</BtnPrimary>
								<BtnPrimary
									size="large"
									class="max-lg:hidden"
									href="/food-recycler"
									onclick={handleReserveClick}
								>
									{$reservationUrl.label}
								</BtnPrimary>
							</div>
						{/if}
					</div>
					{#if pdpPage}
						<div class="flex gap-x-4">
							<Button
								class="btn-tertiary btn-white"
								data-testid="shopping-cart-open-btn"
								title="Open shopping cart"
								onclick={() => shoppingCart.open()}
							>
								<ShoppingCartIcon class="text-brown-2" />
							</Button>
							<BtnPrimary
								title="Add to cart"
								size="small"
								class="lg:hidden"
								onclick={() => {
									shoppingCart.open();
									handleReserveClick();
								}}
								data-testid="btn-header-order-now"
							>
								Add to cart
							</BtnPrimary>
						</div>
					{/if}
				</div>
			</div>
		</nav>
		{#if hasEntries($subNav)}
			<SubNavigation nav={$subNav} />
		{/if}
	</div>
</header>
